import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/mock';
import { MapPin, Phone, Mail, Clock, Send, Instagram, Facebook, Map as MapIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { getCookieConsent, setCookieConsent } from './CookieConsent';
import { trackMetaEvent } from '../lib/metaPixel';
import { trackGAEvent } from '../lib/googleAnalytics';
import { trackCallClick, trackMapClick } from '../lib/trackCall';

const Contact = () => {
  const { t, language } = useLanguage();
  const [mapAllowed, setMapAllowed] = useState(getCookieConsent() === 'accepted');

  useEffect(() => {
    const handler = (e) => setMapAllowed((e.detail || getCookieConsent()) === 'accepted');
    window.addEventListener('cookie-consent-change', handler);
    return () => window.removeEventListener('cookie-consent-change', handler);
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'contacto' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success('Recebemos a sua mensagem, obrigado pelo contacto! A equipa da Pigmentos responde-lhe muito em breve. Enviámos-lhe também um email de confirmação.', { duration: 7000 });
        trackMetaEvent('Lead', { content_name: 'Formulário Contacto' });
        trackGAEvent('generate_lead', { form_name: 'Formulário Contacto' });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error(data.error || 'Não foi possível enviar. Tente novamente ou ligue-nos.');
      }
    } catch {
      toast.error('Não foi possível enviar. Verifique a ligação e tente novamente.');
    } finally {
      setSending(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: t('contact.address'),
      value: companyInfo.address,
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: Phone,
      label: t('contact.phone'),
      // Os tres numeros que a loja tem afixados na porta, pela mesma ordem do cartaz:
      // telemovel de accao (e o unico com WhatsApp), segundo telemovel, fixo.
      multi: [
        {
          text: companyInfo.phone.replace('+351 ', ''),
          href: `tel:${companyInfo.phone.replace(/\s/g, '')}`,
          note: language === 'en' ? 'mobile · WhatsApp' : 'telemóvel · WhatsApp'
        },
        {
          text: companyInfo.phone2.replace('+351 ', ''),
          href: `tel:${companyInfo.phone2.replace(/\s/g, '')}`,
          note: language === 'en' ? 'mobile' : 'telemóvel'
        },
        {
          text: companyInfo.landline.replace('+351 ', ''),
          href: `tel:${companyInfo.landline.replace(/\s/g, '')}`,
          note: language === 'en' ? 'landline' : 'fixo'
        }
      ],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Mail,
      label: t('contact.email'),
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
      color: 'bg-emerald-100 text-emerald-600'
    },
    {
      icon: Clock,
      label: language === 'en' ? 'Opening hours' : 'Horário',
      lines: language === 'en'
        ? ['Mon to Fri: 7:30 AM to 6:30 PM', 'Saturday: 8:00 AM to 1:00 PM', 'Sunday: closed']
        : ['Segunda a sexta: 7h30 às 18h30', 'Sábado: 8h às 13h', 'Domingo: encerrado'],
      note: language === 'en' ? 'Open through lunchtime' : 'Não fecha à hora de almoço',
      color: 'bg-red-100 text-red-600'
    }
  ];

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-600 text-sm font-semibold rounded-full mb-4">
            {t('contact.title')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('contact.subtitle')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto rounded-full" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Side - Contact Info & Map */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="space-y-4">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 p-5 bg-gray-50 rounded-xl hover:shadow-md transition-shadow duration-300"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${info.color}`}>
                    <info.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium mb-1">{info.label}</p>
                    {info.multi ? (
                      <div className="space-y-0.5">
                        {info.multi.map((m, i) => (
                          <p key={i}>
                            <a
                              href={m.href}
                              onClick={() => m.href.startsWith('tel:') && trackCallClick('secção Contactos', m.text)}
                              className="text-gray-900 font-semibold hover:text-orange-600 transition-colors"
                            >
                              {m.text}
                            </a>
                            <span className="text-sm text-gray-500 ml-2">{m.note}</span>
                          </p>
                        ))}
                      </div>
                    ) : info.lines ? (
                      <>
                        <div className="text-gray-900 font-semibold space-y-0.5">
                          {info.lines.map((line, i) => (
                            <p key={i}>{line}</p>
                          ))}
                        </div>
                        {info.note && (
                          <p className="text-sm text-red-600 font-medium mt-1">{info.note}</p>
                        )}
                      </>
                    ) : info.href ? (
                      <a
                        href={info.href}
                        className="text-gray-900 font-semibold hover:text-orange-600 transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-gray-900 font-semibold">{info.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="p-5 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500 font-medium mb-4">{t('contact.social')}</p>
              <div className="flex items-center space-x-4">
                <a
                  href={companyInfo.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-xl flex items-center justify-center text-white hover:shadow-lg transition-shadow duration-300"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a
                  href={companyInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white hover:shadow-lg transition-shadow duration-300"
                >
                  <Facebook className="w-6 h-6" />
                </a>
              </div>
            </div>

            {/* Map — só carrega após consentimento de cookies (o embed do Google instala cookies de terceiros) */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-64 lg:h-80">
              {mapAllowed ? (
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3112.4847821538!2d-9.316721684671855!3d38.69367697960093!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd1ec95e8c9b5555%3A0x4cc8f5b6e9e7b8e8!2sRua%20Jos%C3%A9%20Malhoa%2C%20Oeiras!5e0!3m2!1sen!2spt!4v1629000000000!5m2!1sen!2spt"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pigmentos Location"
                />
              ) : (
                <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center text-center p-6 gap-3">
                  <MapIcon className="w-8 h-8 text-gray-400" />
                  <p className="text-sm text-gray-500 max-w-xs">
                    {language === 'en'
                      ? 'The map uses Google Maps cookies. Accept to load it.'
                      : 'O mapa utiliza cookies do Google Maps. Aceite para o carregar.'}
                  </p>
                  <button
                    onClick={() => setCookieConsent('accepted')}
                    className="px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors"
                  >
                    {language === 'en' ? 'Load map' : 'Carregar mapa'}
                  </button>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address)}`}
                    onClick={() => trackMapClick('secção Contactos')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-orange-600 hover:text-orange-700 underline underline-offset-2"
                  >
                    {language === 'en' ? 'Open in Google Maps' : 'Abrir no Google Maps'}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {t('contact.formTitle')}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="contact-name" className="text-gray-700">
                  {t('contact.form.name')}
                </Label>
                <Input
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-200 focus:border-orange-500"
                  placeholder="Seu nome completo"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-email" className="text-gray-700">
                  {t('contact.form.email')}
                </Label>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-200 focus:border-orange-500"
                  placeholder="seu@email.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-subject" className="text-gray-700">
                  {t('contact.form.subject')}
                </Label>
                <Input
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-200 focus:border-orange-500"
                  placeholder="Assunto da mensagem"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-message" className="text-gray-700">
                  {t('contact.form.message')}
                </Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-white border-gray-200 focus:border-orange-500 resize-none"
                  placeholder="Escreva a sua mensagem..."
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg rounded-xl shadow-lg shadow-orange-600/30 transition-all duration-300 hover:shadow-xl hover:shadow-orange-600/40 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
                {sending ? 'A enviar...' : t('contact.form.submit')}
              </Button>

              {/* Informacao no ponto de recolha, exigida pelo RGPD: este formulario pede nome,
                  email, assunto e mensagem, os quatro obrigatorios, e nao tinha uma unica ligacao
                  a` Politica de Privacidade. A formulacao e' a mesma que ja' esta' publicada no
                  formulario da calculadora, para nao haver duas versoes a circular. */}
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                {language === 'en' ? (
                  <>
                    We use your details only to reply to this request. You can read how we handle
                    your data in our{' '}
                    <a
                      href="/privacidade"
                      className="text-orange-600 underline underline-offset-2 hover:text-orange-700"
                    >
                      Privacy Policy
                    </a>
                    .
                  </>
                ) : (
                  <>
                    Usamos o seu contacto apenas para lhe responder a este pedido. Pode ver como
                    tratamos os dados na{' '}
                    <a
                      href="/privacidade"
                      className="text-orange-600 underline underline-offset-2 hover:text-orange-700"
                    >
                      Política de Privacidade
                    </a>
                    .
                  </>
                )}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
